import type { SilhouetteKind } from '@/lib/quote/vehicle-silhouette'
import type { QuotePhotoAngle } from '@/lib/validation/input/inquiry-schema'

/**
 * Outline drawings for the /quote photo cards: one per body kind and angle,
 * drawn as strokes in currentColor so they read in both themes. The
 * passenger side is the driver side mirrored. Proportions are deliberately
 * cartoonish; the job is to say "stand here, point the camera this way",
 * not to depict the model.
 */

type Paths = { body: string; glass: string; extra?: string; wheels: { cx: number; cy: number; r: number }[] }

const SIDE: Record<SilhouetteKind, Paths> = {
  sedan: {
    body: 'M18 78 L18 64 Q20 57 30 55 L58 52 L80 32 Q85 27 94 27 L132 27 Q142 27 150 36 L166 53 L182 57 Q190 60 190 68 L190 78 Z',
    glass: 'M64 52 L84 34 L128 34 L150 52 Z M106 34 L106 52',
    wheels: [
      { cx: 52, cy: 78, r: 12 },
      { cx: 152, cy: 78, r: 12 },
    ],
  },
  coupe: {
    body: 'M16 78 L16 65 Q18 58 28 56 L72 52 L98 30 Q104 25 114 25 L136 25 Q148 25 158 38 L172 54 L186 58 Q194 61 194 69 L194 78 Z',
    glass: 'M78 52 L102 32 L134 32 L158 52 Z',
    wheels: [
      { cx: 50, cy: 78, r: 12 },
      { cx: 156, cy: 78, r: 12 },
    ],
  },
  suv: {
    body: 'M16 80 L16 58 Q18 50 28 48 L56 46 L72 24 Q76 19 86 19 L168 19 Q178 19 180 30 L184 48 L190 54 L190 80 Z',
    glass: 'M62 46 L78 26 L120 26 L120 46 Z M126 26 L172 26 L178 46 L126 46 Z',
    wheels: [
      { cx: 50, cy: 80, r: 13 },
      { cx: 156, cy: 80, r: 13 },
    ],
  },
  pickup: {
    body: 'M14 80 L14 58 Q16 52 24 50 L52 48 L66 26 Q70 21 78 21 L112 21 Q120 21 120 29 L120 48 L188 48 L192 52 L192 80 Z',
    glass: 'M58 48 L72 28 L114 28 L114 48 Z',
    extra: 'M120 56 L184 56',
    wheels: [
      { cx: 46, cy: 80, r: 13 },
      { cx: 160, cy: 80, r: 13 },
    ],
  },
  van: {
    body: 'M14 82 L14 52 Q16 46 24 44 L40 44 L52 22 Q56 17 64 17 L184 17 Q192 17 192 25 L192 82 Z',
    glass: 'M46 44 L58 24 L96 24 L96 44 Z M104 24 L184 24 L184 44 L104 44 Z',
    wheels: [
      { cx: 48, cy: 82, r: 13 },
      { cx: 160, cy: 82, r: 13 },
    ],
  },
}

const REAR: Record<SilhouetteKind, Paths> = {
  sedan: {
    body: 'M38 84 L38 60 Q38 50 48 44 L62 30 Q66 26 74 26 L126 26 Q134 26 138 30 L152 44 Q162 50 162 60 L162 84 Z',
    glass: 'M70 30 L62 46 L138 46 L130 30 Z',
    extra: 'M38 70 L162 70 M44 60 L60 60 M140 60 L156 60',
    wheels: [
      { cx: 52, cy: 88, r: 5 },
      { cx: 148, cy: 88, r: 5 },
    ],
  },
  coupe: {
    body: 'M36 84 L36 62 Q36 52 48 46 L66 32 Q70 28 78 28 L122 28 Q130 28 134 32 L152 46 Q164 52 164 62 L164 84 Z',
    glass: 'M72 32 L66 46 L134 46 L128 32 Z',
    extra: 'M36 70 L164 70 M42 60 L60 60 M140 60 L158 60',
    wheels: [
      { cx: 50, cy: 88, r: 5 },
      { cx: 150, cy: 88, r: 5 },
    ],
  },
  suv: {
    body: 'M36 86 L36 40 Q36 24 52 22 L148 22 Q164 24 164 40 L164 86 Z',
    glass: 'M48 28 L48 52 L152 52 L152 28 Z',
    extra: 'M36 72 L164 72 M40 60 L54 60 M146 60 L160 60',
    wheels: [
      { cx: 50, cy: 90, r: 5 },
      { cx: 150, cy: 90, r: 5 },
    ],
  },
  pickup: {
    body: 'M34 86 L34 46 L166 46 L166 86 Z',
    glass: 'M60 46 L64 26 Q66 22 72 22 L128 22 Q134 22 136 26 L140 46 M70 28 L68 42 L132 42 L130 28 Z',
    extra: 'M34 74 L166 74 M40 54 L54 54 M146 54 L160 54',
    wheels: [
      { cx: 48, cy: 90, r: 5 },
      { cx: 152, cy: 90, r: 5 },
    ],
  },
  van: {
    body: 'M36 88 L36 28 Q36 20 44 20 L156 20 Q164 20 164 28 L164 88 Z',
    glass: 'M46 28 L46 50 L96 50 L96 28 Z M104 28 L104 50 L154 50 L154 28 Z M100 26 L100 86',
    extra: 'M40 64 L52 64 M148 64 L160 64',
    wheels: [
      { cx: 50, cy: 92, r: 5 },
      { cx: 150, cy: 92, r: 5 },
    ],
  },
}

/**
 * High front-corner view: the camera stands off the front driver corner and
 * looks down, so hood, windshield and roof are all in frame. The car points
 * to the lower left; body = nose, hood and flank, glass = windshield and side
 * glass, extra = roof and headlight.
 */
const CORNER: Record<SilhouetteKind, Paths> = {
  sedan: {
    body: 'M18 68 L18 88 L66 102 L66 82 Z M18 68 L66 82 L134 60 L90 46 Z M66 82 L66 102 L192 68 L192 52 L134 60 Z',
    glass: 'M90 46 L134 60 L142 40 L104 28 Z M134 60 L142 40 L176 31 L192 52 Z',
    extra: 'M104 28 L142 40 L176 31 L140 20 Z M26 76 L46 81',
    wheels: [
      { cx: 48, cy: 102, r: 7 },
      { cx: 168, cy: 76, r: 7 },
    ],
  },
  coupe: {
    body: 'M16 70 L16 88 L66 102 L66 84 Z M16 70 L66 84 L142 62 L100 48 Z M66 84 L66 102 L194 70 L194 56 L142 62 Z',
    glass: 'M100 48 L142 62 L150 44 L114 32 Z M142 62 L150 44 L180 36 L194 56 Z',
    extra: 'M114 32 L150 44 L180 36 L146 25 Z M24 78 L46 83',
    wheels: [
      { cx: 48, cy: 102, r: 7 },
      { cx: 172, cy: 78, r: 7 },
    ],
  },
  suv: {
    body: 'M18 60 L18 90 L66 104 L66 74 Z M18 60 L66 74 L122 58 L78 44 Z M66 74 L66 104 L192 70 L192 46 L122 58 Z',
    glass: 'M78 44 L122 58 L128 36 L88 24 Z M122 58 L128 36 L184 24 L192 46 Z M154 30 L158 52',
    extra: 'M88 24 L128 36 L184 24 L144 12 Z M26 68 L46 74',
    wheels: [
      { cx: 48, cy: 104, r: 7 },
      { cx: 168, cy: 78, r: 7 },
    ],
  },
  pickup: {
    body: 'M18 62 L18 90 L66 104 L66 76 Z M18 62 L66 76 L120 60 L76 46 Z M66 76 L66 104 L128 88 L128 66 L120 60 Z M128 66 L128 88 L196 70 L196 50 L136 52 Z',
    glass: 'M76 46 L120 60 L126 40 L86 26 Z M120 60 L126 40 L150 34 L136 52 Z',
    extra: 'M86 26 L126 40 L150 34 L112 20 Z M136 52 L196 50 M26 70 L46 76',
    wheels: [
      { cx: 48, cy: 104, r: 7 },
      { cx: 174, cy: 82, r: 7 },
    ],
  },
  van: {
    body: 'M20 58 L20 90 L66 104 L66 72 Z M20 58 L66 72 L104 60 L60 44 Z M66 72 L66 104 L194 72 L194 44 L104 60 Z',
    glass: 'M60 44 L104 60 L108 36 L70 22 Z M104 60 L108 36 L190 18 L194 44 Z M150 27 L152 50',
    extra: 'M70 22 L108 36 L190 18 L150 6 Z M28 66 L46 72',
    wheels: [
      { cx: 48, cy: 104, r: 7 },
      { cx: 170, cy: 80, r: 7 },
    ],
  },
}

function Drawing({ paths, mirror }: { paths: Paths; mirror?: boolean }) {
  return (
    <svg
      viewBox='0 0 208 112'
      className='h-full w-full'
      fill='none'
      stroke='currentColor'
      strokeWidth={3}
      strokeLinejoin='round'
      strokeLinecap='round'
      aria-hidden='true'
      focusable='false'
    >
      <g transform={mirror ? 'translate(208 0) scale(-1 1)' : undefined}>
        <path d={paths.body} />
        <path d={paths.glass} strokeWidth={2} opacity={0.7} />
        {paths.extra && <path d={paths.extra} strokeWidth={2} opacity={0.7} />}
        {paths.wheels.map((w, i) => (
          <circle key={i} cx={w.cx} cy={w.cy} r={w.r} />
        ))}
      </g>
    </svg>
  )
}

export function VehicleSilhouette({ kind, angle }: { kind: SilhouetteKind; angle: QuotePhotoAngle }) {
  switch (angle) {
    case 'driver':
      return <Drawing paths={SIDE[kind]} />
    case 'passenger':
      return <Drawing paths={SIDE[kind]} mirror />
    case 'rear':
      return <Drawing paths={REAR[kind]} />
    case 'front-corner':
      return <Drawing paths={CORNER[kind]} />
  }
}
